import { useEffect, useRef, useState, type JSX } from 'react'
import { useSession } from '../auth/auth-context'

/**
 * `/connect/ilmuchat` — the LMS side of ILMUchat's account-link (verify) flow.
 *
 * ILMUchat sends the browser here with `state`, `redirect_uri` and a PKCE
 * `code_challenge`. This page is a thin auth gate:
 *
 * - Not signed in to LMS → bounce to `/login?next=` back here (the user
 *   proves identity at LMS — that is the whole point of verification).
 * - Signed in → full-page navigate to the api's `/connect/authorize`, which
 *   carries the `lms_session` cookie, mints a one-time code, and 302s back
 *   to ILMUchat's `redirect_uri`.
 *
 * No tokens or identity are exposed to JS here — only the opaque `code`
 * transits the browser, and only as a redirect the api builds.
 */
const API_BASE = (import.meta.env.VITE_API_URL ?? '').replace(/\/+$/, '')

export function ConnectIlmuchatPage(): JSX.Element {
  const { user, isLoading } = useSession()
  const [error, setError] = useState<string | null>(null)
  const fired = useRef(false)

  useEffect(() => {
    if (isLoading || fired.current) return

    const params = new URLSearchParams(window.location.search)
    const state = params.get('state')
    const redirectUri = params.get('redirect_uri')
    const codeChallenge = params.get('code_challenge')

    if (!state || !redirectUri || !codeChallenge) {
      setError('This link is missing required parameters. Start again from ILMUchat.')
      return
    }

    if (!user) {
      // Prove identity at LMS first, then return here to complete the link.
      const here = `${window.location.pathname}${window.location.search}`
      window.location.assign(`/login?next=${encodeURIComponent(here)}`)
      return
    }

    fired.current = true
    const authorize = new URL(`${API_BASE}/api/v1/connect/authorize`)
    authorize.searchParams.set('state', state)
    authorize.searchParams.set('redirect_uri', redirectUri)
    authorize.searchParams.set('code_challenge', codeChallenge)
    window.location.assign(authorize.toString())
  }, [user, isLoading])

  return (
    <main className="flex min-h-screen items-center justify-center p-6">
      <div className="max-w-sm text-center">
        <h1 className="text-lg font-semibold text-gray-900">
          Connecting to ILMUchat
        </h1>
        {error ? (
          <p className="mt-2 text-sm text-red-600">{error}</p>
        ) : (
          <p className="mt-2 text-sm text-gray-600">
            Verifying your Leave Hub identity, one moment…
          </p>
        )}
      </div>
    </main>
  )
}
